/**
 * Guards on what enters and leaves `projectKeyComponents`. Three assemblers build
 * `ProjectionKeyInputs` through different SQL, so the engine is the one door every input passes;
 * the negative on-hand that printed -163% came through the two that skip the SQL floor.
 *
 * The engine is shared with the client, which cannot import the request logger, so violations go
 * through a reporter the server installs. Nothing here changes a number.
 */
import type { PeriodStockComponents } from "./engine"
import type { ProjectionKeyInputs } from "./types"

export type ProjectionInvariantViolation = { keyId: string | undefined; problems: string[] }

/** 3dp-rounded cumulative arrays dip by 0.001 between weeks; anything larger is a real decrease. */
export const CUMULATIVE_DIP_TOLERANCE = 0.01
const MAX_REPORTS_PER_MINUTE = 20

let reporter: (violation: ProjectionInvariantViolation) => void = () => {}
let reportMinute = -1
let reportsThisMinute = 0

export function setProjectionInvariantReporter(fn: typeof reporter) {
  reporter = fn
}

/** A negative opening is tolerated: oversold stock is real data and the engine floors it. */
export function validateProjectionInputs(input: ProjectionKeyInputs): string[] {
  const problems: string[] = []
  if (!Number.isFinite(input.opening)) problems.push(`opening ${input.opening}`)
  if (input.salesWeekToDate !== undefined && !(input.salesWeekToDate >= 0)) {
    problems.push(`salesWeekToDate ${input.salesWeekToDate}`)
  }
  input.forecasts.forEach((f, i) => {
    const prev = input.forecasts[i - 1] ?? 0
    if (!Number.isFinite(f)) problems.push(`forecasts[${i}] ${f}`)
    else if (f < prev - CUMULATIVE_DIP_TOLERANCE) {
      problems.push(`forecasts[${i}] ${f} below forecasts[${i - 1}] ${prev}`)
    }
  })
  for (const [name, arrivals] of [
    ["incomingOrders", input.incomingOrders],
    ["forecastedReturns", input.forecastedReturns],
  ] as const) {
    arrivals.forEach((a, i) => {
      if (!Number.isFinite(a.weekDiff)) problems.push(`${name}[${i}].weekDiff ${a.weekDiff}`)
      if (!(Number.isFinite(a.quantity) && a.quantity >= 0)) {
        problems.push(`${name}[${i}].quantity ${a.quantity}`)
      }
    })
  }
  return problems
}

/** Each period's residual is a share of its demand: `0 ≤ lostSales ≤ demand`, within the dip tolerance. */
export function validateProjectionOutput(periods: PeriodStockComponents[]): string[] {
  const problems: string[] = []
  periods.forEach((p, i) => {
    if (!Number.isFinite(p.lostSales) || !Number.isFinite(p.demand)) {
      problems.push(`period[${i}] lostSales ${p.lostSales} demand ${p.demand}`)
    } else if (
      p.lostSales < -CUMULATIVE_DIP_TOLERANCE ||
      p.lostSales > p.demand + CUMULATIVE_DIP_TOLERANCE
    ) {
      problems.push(`period[${i}] lostSales ${p.lostSales} outside [0, ${p.demand}]`)
    }
  })
  return problems
}

export function reportProjectionInvariants(
  input: ProjectionKeyInputs,
  periods: PeriodStockComponents[],
) {
  const problems = [...validateProjectionInputs(input), ...validateProjectionOutput(periods)]
  if (!problems.length) return
  // A systemic data fault hits every key of an org-wide projection; cap what reaches the logger.
  const minute = Math.floor(Date.now() / 60_000)
  if (minute !== reportMinute) {
    reportMinute = minute
    reportsThisMinute = 0
  }
  if (reportsThisMinute++ >= MAX_REPORTS_PER_MINUTE) return
  reporter({ keyId: input.keyId, problems })
}
